<template>
  <div v-if="craftPurchaseCategories" class="modal-card" style="width: auto">
    <header class="modal-card-head">
      <p class="modal-card-title">New Craft</p>
    </header>
    <section class="modal-card-body">
      <b-steps :hasNavigation="false" v-model="activeStep">
        <!--   Categories     -->
        <b-step-item step="1" label="Category">
          <h1 class="title is-size-4 has-text-centered">
            Choose the part of the costume your tutorial will be about
          </h1>
          <h2 class="subtitle is-size-6 has-text-centered">
            Selecting the most relevant category will help users find your item more
            easily.
          </h2>
          <div class="columns is-multiline">
            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success': craft.craft_purchase_category_id === 1 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(1)"
                    :rounded="true"
                    @click="setCraftCategory(1)"
                  >
                    Body
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item is <b>wearable</b> and is part of the costume. <br />
                  <span class="is-italic">Examples: Helmet, Dress, Boots...</span>
                </p>
              </div>
            </div>

            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success': craft.craft_purchase_category_id === 2 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(2)"
                    :rounded="true"
                    @click="setCraftCategory(2)"
                  >
                    Tool
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item is a prop like a <b>weapon</b> or any other <b>tool</b>.<br />
                  <span class="is-italic"
                    >Examples: Sword, Magical staff, Cooking book...</span
                  >
                </p>
              </div>
            </div>

            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success': craft.craft_purchase_category_id === 3 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(3)"
                    :rounded="true"
                    @click="setCraftCategory(3)"
                  >
                    Accessory
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item is an <b>accessory</b> wearable as a part of the costume.<br />
                  <span class="is-italic">Examples: Jewel, mask, pet...</span>
                </p>
              </div>
            </div>

            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success': craft.craft_purchase_category_id === 4 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(4)"
                    :rounded="true"
                    @click="setCraftCategory(4)"
                  >
                    Make-up
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item is part of a <b>make-up</b> or the make-up itself. <br />
                  <span class="is-italic"
                    >Examples: Make-up, latex, body-paint, SFX, lenses, ...</span
                  >
                </p>
              </div>
            </div>

            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success': craft.craft_purchase_category_id === 5 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(5)"
                    :rounded="true"
                    @click="setCraftCategory(5)"
                  >
                    Wig
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item is a <b>wig</b> or part of it. <br />
                  <span class="is-italic"
                    >Examples: Wig styling, wig creation, wig dyeing...</span
                  >
                </p>
              </div>
            </div>
            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success ': craft.craft_purchase_category_id === 6 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(6)"
                    :rounded="true"
                    @click="setCraftCategory(6)"
                  >
                    Stage prop
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item is a <b>prop for stage</b> performances or
                  <b>photoshoot decoration.</b> <br />
                  <span class="is-italic"
                    >Examples: Cardboard tree, throne, magician props...</span
                  >
                </p>
              </div>
            </div>
            <div class="column has-text-centered">
              <div class="box fullheightcustom is-primary">
                <div class="p-3">
                  <b-button
                    :type="{ 'is-success': craft.craft_purchase_category_id === 7 }"
                    size="is-medium"
                    :icon-left="craftPurchaseGetIconName(7)"
                    :rounded="true"
                    @click="setCraftCategory(7)"
                  >
                    Other
                  </b-button>
                </div>
                <p class="has-text-dark">
                  The item <b>is not included in previous sections.</b> <br />
                  <span class="is-italic"
                    >Please note that categorizing properly increases visibility in search
                    results.</span
                  >
                </p>
              </div>
            </div>
          </div>
        </b-step-item>

        <!--    Skills    -->

        <b-step-item step="2" label="Skills">
          <h1 class="title is-size-4 has-text-centered">
            Select skills that the student will improve through following your tutorial
          </h1>
          <h2 class="subtitle is-size-6 has-text-centered">
            Selecting the most relevant skills will help users find your item more easily.
          </h2>

          <div class="columns is-centered">
            <div class="column is-8">
              <craft-skills
                :skill_ids="craft.skill_ids"
                @updateSkills="craft.skill_ids = $event"
              />
            </div>
          </div>
        </b-step-item>
        <b-step-item step="3" label="Materials">
          <h1 class="title is-size-4 has-text-centered">
            Materials used in order to realize your tutorial
          </h1>
          <h2 class="subtitle is-size-6 has-text-centered">
            Fill in the stores name to be refered on their page.
          </h2>

          <p class="has-text-centered is-italic is-gray">Coming soon~</p>

          <!-- <p class="pb-2 has-text-weight-semibold is-size-6">Materials</p>
            <br />
            <b-field grouped>
              <b-field label="Amount" label-position="on-border" expanded>
                <b-field>
                  <b-input placeholder="e.g. 2 XL sheets" expanded></b-input>
                </b-field>
              </b-field>
              <b-field label="Material" label-position="on-border" expanded>
                <b-field>
                  <b-input placeholder="e.g. Black Worbla" expanded></b-input>
                </b-field>
              </b-field>
              <b-field label="Store" label-position="on-border" expanded>
                <b-field>
                  <b-input placeholder="e.g. Cosplay Shop" expanded></b-input>
                </b-field>
              </b-field>
              <p class="control">
                <b-button class="button is-success is-inverted"
                  ><b-icon type="" icon="plus"
                /></b-button>
              </p>
            </b-field>

            <b-field grouped>
              <b-field label="Amount" label-position="on-border" expanded>
                <b-field>
                  <b-input placeholder="e.g. 2 XL sheets" expanded></b-input>
                </b-field>
              </b-field>
              <b-field label="Material" label-position="on-border" expanded>
                <b-field>
                  <b-input placeholder="e.g. Black Worbla" expanded></b-input>
                </b-field>
              </b-field>
              <b-field label="Store" label-position="on-border" expanded>
                <b-field>
                  <b-input placeholder="e.g. Cosplay Shop" expanded></b-input>
                </b-field>
              </b-field>
              <p class="control">
                <b-button class="button is-danger is-inverted"
                  ><b-icon type="" icon="delete"
                /></b-button>
              </p>
            </b-field> -->
        </b-step-item>

        <b-step-item step="4" label="Summary">
          <ValidationObserver ref="observer4" v-slot="{ handleSubmit }">
            <h1 class="title is-size-4 has-text-centered">
              More details about your new Craft tutorial
            </h1>
            <h2 class="subtitle is-size-6 has-text-centered">
              Fill in the details so that users know what this tutorial is about.
            </h2>

            <item-title
              :title="craft.name"
              :itemType="'craft'"
              :displaySuccess="false"
              @updateTitle="craft.name = $event"
            />

            <item-description
              :description="craft.description"
              @updateDescription="craft.description = $event"
            />

            <item-languages
              :languages="craft.language_list"
              :maxTags="'1'"
              @updateLanguages="craft.language_list = $event"
            />

            <item-video :video="craft.video" @updateVideo="craft.video = $event" />

            <item-visibility
              :hidden="craft.hidden"
              :itemType="'craft'"
              @updateHidden="craft.hidden = $event"
            />

            <item-tags
              :tags="craft.tag_list"
              :placeholder="'WCS, Comic Con, Photoshop...'"
              @updateTags="craft.tag_list = $event"
            />
          </ValidationObserver>
        </b-step-item>
      </b-steps>
    </section>

    <footer class="modal-card-foot">
      <span class="card-footer-item">
        <b-button
          @click="previousStep"
          :disabled="onFirstStep"
          type="is-danger"
          icon-left="skip-previous"
        >
          Previous
        </b-button>
      </span>

      <span class="card-footer-item">
        <b-button
          @click="nextStep"
          v-if="!onLastStep"
          :disabled="craft.craft_purchase_category_id == null"
          type="is-success"
          icon-right="skip-next"
        >
          Next
        </b-button>

        <b-button @click="createCraft" v-else type="is-success" icon-right="check">
          Create
        </b-button>
      </span>

      <span class="card-footer-item">
        <b-button @click="$parent.close()"> Close </b-button>
      </span>
    </footer>
  </div>
</template>

<script>
// Mixins
import { craftPurchaseCategoryMixin } from '@/mixins/craftPurchaseCategoryMixin'
import { notificationMixin } from '@/mixins/notificationMixin'
import { formStepsMixin } from '@/mixins/formStepsMixin'

// Components
import { ValidationObserver } from 'vee-validate'

// Attributes
import CraftSkills from '@/components/Attributes/CraftSkills'
import ItemTitle from '@/components/Attributes/ItemTitle'
import ItemDescription from '@/components/Attributes/ItemDescription'
import ItemLanguages from '@/components/Attributes/ItemLanguages'
import ItemVideo from '@/components/Attributes/ItemVideo'
import ItemTags from '@/components/Attributes/ItemTags'
import ItemVisibility from '@/components/Attributes/ItemVisibility'

export default {
  mixins: [craftPurchaseCategoryMixin, notificationMixin, formStepsMixin],
  components: {
    ValidationObserver,
    CraftSkills,
    ItemTitle,
    ItemDescription,
    ItemLanguages,
    ItemVideo,
    ItemTags,
    ItemVisibility
  },
  props: ['project'],
  data () {
    return {
      steps: 4,
      selectedPart: null,
      craft: {
        name: null,
        description: null,
        video: null,
        tag_list: [],
        craft_purchase_category_id: null,
        skill_ids: [],
        hidden: true,
        language_list: []
      },
      craftPurchaseCategories: null,
      // skills: null,
      // skillsByCategory: null,
      checkboxGroup: null
    }
  },
  created () {
    this.$store
      .dispatch('craftPurchaseCategories/index')
      .then((resp) => {
        this.craftPurchaseCategories = resp.data.data
      })
      .catch((error) => {
        console.log(error.response)
        this.alertError()
      })
    // this.$store
    //   .dispatch('skills/index')
    //   .then((response) => {
    //     this.skills = response.data.data
    //     let skillsAttributes = this.skills.map((skill) => skill.attributes)
    //     this.skillsByCategory = _.groupBy(skillsAttributes, 'category')
    //   })
    //   .catch((error) => {
    //     console.log(error.response)
    //     this.alertError()
    //   })
  },
  methods: {
    createCraft () {
      this.$refs.observer4.validate().then((success) => {
        if (success) {
          let payload = { project: this.project.id, craft: this.craft }
          this.$store
            .dispatch('projects/crafts/create', payload)
            .then((resp) => {
              this.$router.push(`/crafts/${resp.data.data.id}`)
            })
            .catch((error) => {
              console.log(error.response)
              this.alertError()
            })
        }
      })
    },
    setCraftCategory (categoryId) {
      this.craft.craft_purchase_category_id = categoryId
    }
  }
}
</script>
